import { CreatorOrderV2ContractError } from '@/apis/creatorOrderV2/errors';
import type {
  CreatorChannelQuoteItem,
  CreatorJoinRequest,
  CreatorNegotiateRequest,
  CreatorOrderPlatform,
  CreatorQuotePreviewRequest
} from '@/interface/creatorOrderV2';
import type { CreatorCollaborationPackage } from '@/biz/multiPlatformCollaboration/model';
import { getCreatorSubmittedContentType } from './submittedContentType';
import {
  type PackageRates,
  validateCreatorPackageRate
} from '@/biz/multiPlatformCollaboration/packageRates';

type CreatorCounterRequest = Extract<
  CreatorNegotiateRequest,
  { action: 'counter' }
>;

const assertCreatorPackageRate = ({
  packageRate,
  channels
}: {
  packageRate: number;
  channels: CreatorChannelQuoteItem[];
}) => {
  const result = validateCreatorPackageRate({
    packageRate,
    channelTotal: channels.reduce((sum, channel) => sum + channel.amount, 0)
  });
  if (result.isValid) return;

  throw new CreatorOrderV2ContractError(
    result.reason === 'PACKAGE_ABOVE_CHANNEL_TOTAL'
      ? `Package rate cannot exceed the channel total of ${result.maximum}.`
      : 'A positive package rate is required.'
  );
};

const buildCreatorChannelQuoteItems = ({
  collaborationPackage,
  rates,
  includedPlacementIds,
  contentTypes = {}
}: {
  collaborationPackage: CreatorCollaborationPackage;
  rates: PackageRates;
  includedPlacementIds?: string[];
  contentTypes?: Record<string, string>;
}): CreatorChannelQuoteItem[] => {
  const included = includedPlacementIds
    ? new Set(includedPlacementIds)
    : undefined;
  const placements = collaborationPackage.placements.filter(
    (placement) =>
      placement.raw?.active !== false &&
      (!included || included.has(placement.id))
  );
  if (included && placements.length !== included.size) {
    throw new CreatorOrderV2ContractError(
      'The selected channel set contains a channel that is unavailable.'
    );
  }

  return placements.map((placement) => {
    const channelKey = String(placement.channelKey || '').trim();
    const platformId = String(placement.platformId || '').trim();
    const contentType = String(
      contentTypes[placement.id] || placement.contentType || ''
    ).trim();
    const contentGenre = String(
      placement.raw?.contentGenre ??
        placement.raw?.interestedPriceFields?.[0]?.contentGenre ??
        placement.raw?.priceFields?.[0]?.contentGenre ??
        ''
    ).trim();
    const channelAmount = Number(rates[placement.id]);
    if (
      !channelKey ||
      !platformId ||
      !contentType ||
      !Number.isFinite(channelAmount) ||
      channelAmount <= 0
    ) {
      throw new CreatorOrderV2ContractError(
        `Cannot submit a V2 quote for channel ${placement.id}: channelKey, platformId, contentType and a positive amount are required.`
      );
    }
    return {
      channelKey,
      platform: placement.platform as CreatorOrderPlatform,
      platformId,
      amount: channelAmount,
      contentType,
      ...(contentGenre ? { contentGenre } : {})
    };
  });
};

/** Keep preview channel selection and prices identical to the eventual quote. */
export const buildCreatorV2QuotePreviewRequest = ({
  collaborationPackage,
  rates,
  includedPlacementIds,
  amount,
  contentTypes
}: {
  collaborationPackage: CreatorCollaborationPackage;
  rates: PackageRates;
  includedPlacementIds: string[];
  amount: number;
  contentTypes: Record<string, string>;
}): CreatorQuotePreviewRequest => {
  const channels = buildCreatorChannelQuoteItems({
    collaborationPackage,
    rates,
    includedPlacementIds,
    contentTypes
  });
  if (!channels.length || !Number.isFinite(amount) || amount <= 0) {
    throw new CreatorOrderV2ContractError(
      'V2 quote preview requires a positive package amount and at least one channel.'
    );
  }
  assertCreatorPackageRate({ packageRate: amount, channels });
  return { channels, amount };
};

/** First application uses the V2 join DTO; action and lastBid never belong here. */
export const buildCreatorV2InitialQuoteRequest = ({
  collaborationPackage,
  rates,
  includedPlacementIds,
  amount,
  nonNegotiable,
  message,
  contentTypes,
  productShippingCountry,
  screeningQuestions,
  scheduleConflict,
  hasCompetitivePrice
}: {
  collaborationPackage: CreatorCollaborationPackage;
  rates: PackageRates;
  includedPlacementIds: string[];
  amount: number;
  nonNegotiable?: boolean;
  message: string;
  contentTypes: Record<string, string>;
  productShippingCountry?: string;
  screeningQuestions?: Array<{
    question: string;
    answer?: string;
    [key: string]: unknown;
  }>;
  scheduleConflict?: CreatorJoinRequest['scheduleConflict'];
  hasCompetitivePrice?: boolean;
}): CreatorJoinRequest => {
  const channels = buildCreatorChannelQuoteItems({
    collaborationPackage,
    rates,
    includedPlacementIds,
    contentTypes
  });
  const packageAmount = Number(amount);
  if (
    channels.length === 0 ||
    !Number.isFinite(packageAmount) ||
    packageAmount <= 0
  ) {
    throw new CreatorOrderV2ContractError(
      'V2 join requires a positive package amount and at least one channel.'
    );
  }
  assertCreatorPackageRate({ packageRate: packageAmount, channels });
  const normalizedQuestions = screeningQuestions?.map((item) => ({
    question: item.question.trim(),
    answer: item.answer?.trim() ?? ''
  }));
  if (normalizedQuestions?.some((item) => !item.question || !item.answer)) {
    throw new CreatorOrderV2ContractError(
      'Every screening question requires its question and answer.'
    );
  }
  const shippingCountry = productShippingCountry?.trim().toUpperCase();

  return {
    channels,
    amount: packageAmount,
    ...(typeof nonNegotiable === 'boolean' ? { nonNegotiable } : {}),
    ...(message.trim() ? { message: message.trim() } : {}),
    ...(shippingCountry ? { productShippingCountry: shippingCountry } : {}),
    ...(normalizedQuestions?.length
      ? { screeningQuestions: normalizedQuestions }
      : {}),
    ...(scheduleConflict ? { scheduleConflict } : {}),
    ...(typeof hasCompetitivePrice === 'boolean' ? { hasCompetitivePrice } : {})
  };
};

// The editor and request builder must use the same ACTIVE channel scope.
// Keep the original package intact for the historical snapshot viewer.
export const getCreatorCounterofferPackage = (
  collaborationPackage: CreatorCollaborationPackage | null
): CreatorCollaborationPackage | null =>
  collaborationPackage
    ? {
        ...collaborationPackage,
        placements: collaborationPackage.placements
          .filter((placement) => placement.raw?.active !== false)
          .map((placement) => ({
            ...placement,
            contentType: getCreatorSubmittedContentType(
              collaborationPackage,
              placement
            )
          }))
      }
    : null;

export const getCreatorV2LastBid = (order: Record<string, any>) => {
  const history = Array.isArray(order?.negotiationHistory)
    ? order.negotiationHistory
    : undefined;
  if (history?.length) return history.length;

  // PRIMARY detail also exposes quoteRevision. Keep it only as a fallback for
  // projections that omit history; it represents the same revision count.
  const revision = order?.quoteRevision;
  if (Number.isSafeInteger(revision) && revision > 0) return revision;

  throw new CreatorOrderV2ContractError(
    'Cannot negotiate before the first application or without a quote revision.'
  );
};

export const buildCreatorV2ApproveRequest = (
  order: Record<string, any>
): CreatorNegotiateRequest => ({
  action: 'approve',
  lastBid: getCreatorV2LastBid(order)
});

export const buildCreatorV2CounterRequest = ({
  order,
  collaborationPackage,
  includedPlacementIds,
  rates,
  amount,
  nonNegotiable,
  message
}: {
  order: Record<string, any>;
  collaborationPackage: CreatorCollaborationPackage;
  includedPlacementIds?: string[];
  rates: PackageRates;
  amount: number;
  nonNegotiable?: boolean;
  message?: string;
}): CreatorCounterRequest => {
  const channels = buildCreatorChannelQuoteItems({
    // After application, only prices and notes can change. Resolve types from
    // persisted data, never from an editor's stale content-type selection.
    collaborationPackage: getCreatorCounterofferPackage({
      ...collaborationPackage,
      raw: order
    })!,
    includedPlacementIds,
    rates
  });

  const packageAmount = Number(amount);
  if (
    channels.length === 0 ||
    !Number.isFinite(packageAmount) ||
    packageAmount <= 0
  ) {
    throw new CreatorOrderV2ContractError(
      'V2 counteroffer requires a positive package amount and at least one ACTIVE channel.'
    );
  }
  assertCreatorPackageRate({
    packageRate: packageAmount,
    channels
  });

  return {
    action: 'counter',
    lastBid: getCreatorV2LastBid(order),
    amount: packageAmount,
    nonNegotiable,
    message: message?.trim() || undefined,
    channels
  };
};
